import { Dropdown } from '@/components/Dropdown/Dropdown';
import { CopyLinkMenuItem } from '@/components/Dropdown/MenuItems/CopyLinkMenuItem';
import { DeleteMenuItem } from '@/components/Dropdown/MenuItems/DeleteMenuItem';
import { DownloadMenuItem } from '@/components/Dropdown/MenuItems/DownloadMenuItem';
import { ExportSubtitlesMenuItem } from '@/components/Dropdown/MenuItems/ExportSubtitlesMenuItem';
import { RawDataMenuItem, shouldShowRawMenuItem } from '@/components/Dropdown/MenuItems/RawDataMenuItem';
import { RenameMenuItem } from '@/components/Dropdown/MenuItems/RenameMenuItem';
import { SaveAsTemplateMenuItem } from '@/components/Dropdown/MenuItems/SaveAsTemplateMenuItem';
import { ShareMenuItem } from '@/components/Dropdown/MenuItems/ShareMenuItem';
import { ShareMenuContent } from '@/components/ShareMenu/ShareMenu';
import { ThreeDotsCircle } from '@/components/ThreeDotsCircle';
import { EditableTitle } from '@/components/Title/EditableTitle';
import { toast } from '@/components/toast';
import { useIsSubtitlesEnabled } from '@/hooks/useIsSubtitlesEnabled';
import { PolotnoStoreKeys } from '@/pages/AdvancedEditor/hooks';
import { PROJECT_IDB_DRAFT_QUERY_KEY, useProjectIdb } from '@/pages/AdvancedEditor/hooks/polotno-store/store-init/useProjectIdb';
import { newProjectId } from '@/pages/AdvancedEditor/types';
import { EditAsNewMenuItem } from '@/pages/video-library/components/VideoOptionsList/Options/EditAsNewMenuItem';
import { EditMenuItem } from '@/pages/video-library/components/VideoOptionsList/Options/EditMenuItem';
import { useAnalytics } from '@/services/analytics';
import { createShareRecord, updateVideo } from '@/services/api';
import { useAuth } from '@/services/auth';
import { useFlag } from '@/services/featureFlags';
import { logger } from '@/services/logger';
import { useRefetchVideos } from '@/state/video-library/videoLibrary';
import { createShareUrl, sendShareMixpanelEvent } from '@/utils/shareUtils';
import { DEFAULT_VIDEO_NAME } from '@d-id/studio/src/state/presenters/presenters';
import { LibraryItem, LibraryItemStatus } from '@d-id/types';
import styled from '@emotion/styled';
import DOMPurify from 'dompurify';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { atom, useSetRecoilState } from 'recoil';

export const libraryItemToDelete = atom<LibraryItem | null>({
    key: 'library-item-to-delete',
    default: null,
});

import {
    MenuDateText,
    MenuOptionsButtonWrapper,
    MenuTitleContainer,
    StyledMenuDivider,
    StyledMenuList,
} from '@/components/VideoTile/videoTileMenuStyles';

const VideoTitle = styled(EditableTitle)`
    font-weight: 700;
    font-size: 20px;
    letter-spacing: 0.05rem;
    overflow: hidden;

    &:focus,
    &:hover {
        border: none;
    }

    &:not(:focus) {
        text-overflow: ellipsis;
    }
`;

const dateFormatOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };

interface Props {
    video: LibraryItem;
    onOpen?: () => void;
    onClose?: () => void;
    origin: string;
}

export const VideoOptionsList = ({ video, onOpen, onClose, origin }: Props) => {
    const mixpanel = useAnalytics();
    const { refetchVideos } = useRefetchVideos();
    const subtitlesEnabled = useIsSubtitlesEnabled();
    const { user } = useAuth();
    const projectsTemplateAdmin = useFlag('projects_template_admin', false);
    const projectTemplateCreator = useFlag('project_template_creator', false);

    const titleRef = useRef<HTMLDivElement | null>(null);
    const [videoName, setVideoName] = useState('');
    const [closed, setClosed] = useState<boolean>(true);
    const setItemToDelete = useSetRecoilState(libraryItemToDelete);
    const { idbDraft, saveIdbDraft } = useProjectIdb();
    const queryClient = useQueryClient();

    const showSaveAsTemplateItem = (projectsTemplateAdmin || projectTemplateCreator) && [LibraryItemStatus.Done].includes(video.status);

    useEffect(() => {
        if (video.name) {
            setVideoName(video.name);
        }
    }, [video.name]);

    const updateName = async (video: LibraryItem, name: string) => {
        try {
            const sanitizedName = DOMPurify.sanitize(name);
            if (video.id === newProjectId) {
                const draft = idbDraft.data;
                if (draft) {
                    const updatedDraft = {
                        ...draft,
                        store: {
                            ...draft.store,
                            custom: { ...draft.store.custom, [PolotnoStoreKeys.creativeVideoName]: name },
                        },
                    };
                    await saveIdbDraft({ ...updatedDraft, videoName: name });
                    queryClient.invalidateQueries(PROJECT_IDB_DRAFT_QUERY_KEY);
                }
            } else {
                await updateVideo(video.id, { name: sanitizedName.trim() || DEFAULT_VIDEO_NAME }, video.type);
                await refetchVideos();
            }
        } catch (e) {
            toast.error({ title: 'Failed to update video name, please try again' });
            logger.error('Failed to update video name', { e });
            setVideoName(video.name);
        }
    };

    const handleCloseMenu = () => {
        setClosed(true);
        onClose?.();
    };

    const handleOpenMenu = () => {
        setClosed(false);
        onOpen?.();
    };

    const handleShare = (option: string) => {
        createShareRecord(video);

        sendShareMixpanelEvent({
            modal: 'play',
            item: video,
            to: option,
            from: origin,
        });

        handleCloseMenu();
    };

    const items = [
        { label: <CopyLinkMenuItem video={video} closeMenu={handleCloseMenu} origin={origin} /> },
        {
            label: <ShareMenuItem video={video} />,
            items: [
                {
                    label: (
                        <ShareMenuContent
                            testId={`video-gallery-share-menu-content-${videoName}`}
                            createShareUrl={(option: string) => createShareUrl({ itemId: video.id, from: option, type: 'video' })}
                            handleShare={handleShare}
                        />
                    ),
                },
            ],
        },
        { label: <EditAsNewMenuItem video={video} closeMenu={handleCloseMenu} /> },
        { label: <EditMenuItem video={video} closeMenu={handleCloseMenu} /> },
        { label: <RenameMenuItem video={video} titleRef={titleRef} /> },
        { label: <DownloadMenuItem video={video} closeMenu={handleCloseMenu} /> },
        {
            label: (
                <ExportSubtitlesMenuItem
                    show={subtitlesEnabled && video!.subtitles}
                    video={video}
                    closeMenu={handleCloseMenu}
                    origin={origin}
                />
            ),
        },
        {
            label: ![LibraryItemStatus.Created, LibraryItemStatus.Started, LibraryItemStatus.Error].includes(video.status) ? (
                <StyledMenuDivider />
            ) : null,
        },
        {
            label: (
                <DeleteMenuItem
                    video={video}
                    closeMenu={handleCloseMenu}
                    onDelete={() => {
                        setItemToDelete(video);
                    }}
                />
            ),
        },
    ];

    if (shouldShowRawMenuItem(user)) {
        items.push({
            label: <StyledMenuDivider />,
        });
        items.push({
            label: <RawDataMenuItem dataToShow={video} closeMenu={handleCloseMenu} />,
        });
    }

    if (showSaveAsTemplateItem) {
        items.push({
            label: <SaveAsTemplateMenuItem projectId={video.id} closeMenu={handleCloseMenu} />,
        });
    }

    return (
        <Dropdown
            closed={closed}
            onOpen={handleOpenMenu}
            onClose={handleCloseMenu}
            trigger={
                <MenuOptionsButtonWrapper>
                    <ThreeDotsCircle data-testid={`video-options-three-dots-circle-${videoName}`} isActive={!closed} />
                </MenuOptionsButtonWrapper>
            }
            items={items}
            renderMenuWrapper={children => (
                <StyledMenuList>
                    <MenuTitleContainer
                        onKeyDown={e => {
                            e.stopPropagation();
                        }}>
                        <VideoTitle
                            ref={titleRef}
                            fontSize={20}
                            disabled={video.is_default}
                            value={videoName}
                            onChange={value => {
                                setVideoName(value);
                            }}
                            onBlur={value => {
                                mixpanel?.track('video-name', { event: 'click', value, from: 'video-menu' });
                                updateName(video, value);
                            }}
                        />
                    </MenuTitleContainer>
                    <MenuDateText>Created {new Date(video.created_at).toLocaleDateString('en-US', dateFormatOptions)}</MenuDateText>
                    <StyledMenuDivider />
                    {children}
                </StyledMenuList>
            )}
        />
    );
};
